import type { ViewEventData } from '@moment-golf/types';
import { resolveRoundScoreArrayIndexForRound } from '@moment-golf/shared';

export function clampStartHole(raw: unknown, totalHoles: number): number {
    const n = Number(raw);
    const max = Math.max(1, Math.round(totalHoles) || 18);
    if (!Number.isFinite(n) || n <= 0) return 1;
    return Math.min(Math.max(Math.round(n), 1), max);
}

/** Treat Mongo / form string booleans as real booleans */
export function effectiveRoundBool(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        const s = value.toLowerCase().trim();
        return s === 'true' || s === '1' || s === 'yes';
    }
    return false;
}

function patchScoreForRound(score: any, roundNumber: number, startHole: number): any {
    if (Array.isArray(score.roundScores) && score.roundScores.length > 0) {
        const idx = resolveRoundScoreArrayIndexForRound(score.roundScores, roundNumber);
        if (idx < 0) return score;
        return {
            ...score,
            roundScores: score.roundScores.map((rs: any, i: number) =>
                i === idx ? { ...rs, startingHole: startHole } : rs,
            ),
        };
    }

    if (Number(score.roundNumber) === roundNumber) {
        return { ...score, startingHole: startHole };
    }

    return score;
}

/**
 * When the round is not shotgun and not split tees, push `startHole` onto every registration
 * tee-time row and score row for that round so live scoring and CreateEventScores stay aligned.
 * Returns only keys that should be merged (avoids wiping `registrations`/`scores` when absent).
 */
export function applyUniformStartHoleToViewEventData(
    viewEventData: ViewEventData,
    roundNumber: number,
    startHoleRaw: unknown,
    roundCfg: { totalHoles?: number },
): Partial<Pick<ViewEventData, 'registrations' | 'scores'>> {
    const startHole = clampStartHole(startHoleRaw, Number(roundCfg.totalHoles) || 18);
    const out: Partial<Pick<ViewEventData, 'registrations' | 'scores'>> = {};

    if (Array.isArray(viewEventData.registrations)) {
        out.registrations = viewEventData.registrations.map((reg: any) => {
            if (!Array.isArray(reg.teeTimes)) return reg;
            return {
                ...reg,
                teeTimes: reg.teeTimes.map((tt: any) =>
                    Number(tt.round) === roundNumber ? { ...tt, startingHole: startHole } : tt,
                ),
            };
        });
    }

    if (Array.isArray(viewEventData.scores)) {
        out.scores = viewEventData.scores.map((score: any) =>
            patchScoreForRound(score, roundNumber, startHole),
        );
    }

    return out;
}
